import { DetectionZoneCardinalDirection, LaneLine, TurnType } from "@app/shared";
import { getGeocode } from "use-places-autocomplete";
import { ICoordsHelper } from "./coordsHelpers";

export const LANE_NUMBER_COLOR_PALETTE: readonly string[] = [
    "#d0021b", // 1 — red
    "#f5a623", // 2 — orange
    "#f8e71c", // 3 — yellow
    "#8b572a", // 4 — brown
    "#4a90e2", // 5 — blue
    "#7ed321", // 6 — green
    "#bd10e0", // 7 — purple
    "#50e3c2", // 8 — teal
];

export const UNKNOWN_LANE_COLOR = "#6b7280";

export function colorForLaneNumber(laneNumber: number | null | undefined): string {
    if (typeof laneNumber !== "number" || !Number.isFinite(laneNumber) || laneNumber < 1) {
        return UNKNOWN_LANE_COLOR;
    }
    const idx = (Math.floor(laneNumber) - 1) % LANE_NUMBER_COLOR_PALETTE.length;
    return LANE_NUMBER_COLOR_PALETTE[idx];
}

export const getCardinalDirectionAbbreviation = (direction: DetectionZoneCardinalDirection): string => {
    switch (direction) {
        case DetectionZoneCardinalDirection.North:
            return "NB";
        case DetectionZoneCardinalDirection.NorthEast:
            return "NEB";
        case DetectionZoneCardinalDirection.East:
            return "EB";
        case DetectionZoneCardinalDirection.SouthEast:
            return "SEB";
        case DetectionZoneCardinalDirection.South:
            return "SB";
        case DetectionZoneCardinalDirection.SouthWest:
            return "SWB";
        case DetectionZoneCardinalDirection.West:
            return "WB";
        case DetectionZoneCardinalDirection.NorthWest:
            return "NWB";
        default:
            return "Unknown";
    }
};

export const getCardinalDirectionWithBound = (direction: DetectionZoneCardinalDirection): string => {
    switch (direction) {
        case DetectionZoneCardinalDirection.North:
            return "North Bound";
        case DetectionZoneCardinalDirection.NorthEast:
            return "North East Bound";
        case DetectionZoneCardinalDirection.East:
            return "East Bound";
        case DetectionZoneCardinalDirection.SouthEast:
            return "South East Bound";
        case DetectionZoneCardinalDirection.South:
            return "South Bound";
        case DetectionZoneCardinalDirection.SouthWest:
            return "South West Bound";
        case DetectionZoneCardinalDirection.West:
            return "West Bound";
        case DetectionZoneCardinalDirection.NorthWest:
            return "North West Bound";
        default:
            return "Unknown";
    }
};

export const getStreetNameFromLocation = async (lat: number, lng: number): Promise<string | null> => {
    try {
        const results = await getGeocode({ location: { lat, lng } });

        if (results && results.length > 0) {
            for (let i = 0; i < results.length; i++) {
                const result = results[i];
                if (result.types && result.types.includes("route")) {
                    const routeComponent = result.address_components?.find((component: any) =>
                        component.types.includes("route")
                    );
                    if (routeComponent) {
                        return routeComponent.long_name;
                    }
                }
            }

            for (let i = 0; i < results.length; i++) {
                const addressComponents = results[i].address_components;
                const routeComponent = addressComponents?.find((component: any) =>
                    component.types.includes("route")
                );
                if (routeComponent) {
                    return routeComponent.long_name;
                }
            }

            return null;
        }
    } catch (error) {
        console.error("Error getting street name:", error);
    }
    return null;
};

export interface DetectedStreetInfo {
    firstStreet: string | null;
    lastStreet: string | null;
    formattedName: string;
}

export const detectLaneLineStreetName = async (
    laneLine: LaneLine,
    direction: DetectionZoneCardinalDirection,
    coordsHelper: ICoordsHelper
): Promise<DetectedStreetInfo | null> => {
    if (!laneLine.points || laneLine.points.length < 2) {
        return null;
    }

    const firstPoint = laneLine.points[0];
    const lastPoint = laneLine.points[laneLine.points.length - 1];

    const firstLatLng = coordsHelper.toLatLng(firstPoint);
    const lastLatLng = coordsHelper.toLatLng(lastPoint);

    const directionAbbr = getCardinalDirectionAbbreviation(direction);
    const turnTypeName = (laneLine.turnType !== undefined && laneLine.turnType !== null)
        ? TurnType[laneLine.turnType]
        : "Unknown";

    const [firstStreet, lastStreet] = await Promise.all([
        getStreetNameFromLocation(firstLatLng.lat(), firstLatLng.lng()),
        getStreetNameFromLocation(lastLatLng.lat(), lastLatLng.lng())
    ]);

    if (!firstStreet && !lastStreet) {
        return null;
    }

    let formattedName = "";
    if (turnTypeName === "Straight" && firstStreet === lastStreet) {
        formattedName = `(${directionAbbr}) ${firstStreet || lastStreet || "Unknown"} - Straight`;
    } else {
        const start = firstStreet || "Unknown";
        const end = lastStreet || "Unknown";
        formattedName = `(${directionAbbr}) ${start} - ${turnTypeName} - ${end}`;
    }

    return {
        firstStreet,
        lastStreet,
        formattedName
    };
};

export const generateUniqueLaneName = (
    baseName: string,
    existingLaneLines: LaneLine[],
    currentLaneLineId?: string
): string => {
    const otherLaneLines = existingLaneLines.filter(ll => ll.id !== currentLaneLineId);

    let finalName = baseName;
    let counter = 2;

    while (otherLaneLines.some(ll => ll.name === finalName)) {
        finalName = `${baseName} - ${counter}`;
        counter++;
    }

    return finalName;
};
